import { useState, useEffect, useCallback } from 'react';
import { 
  Users, Plus, Building, Calendar, DollarSign, Check, 
  FileText, Eye, X, User, Phone, CreditCard, AlertCircle, RefreshCw
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle
} from '@/components/ui/alert-dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '@/components/ui/table';
import { Textarea } from '@/components/ui/textarea';
import { useToast } from '@/hooks/use-toast';
import { Toaster } from '@/components/ui/toaster';
import { storesAPI, manufacturersAPI } from '@/services/api';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Helper to format date in Indian format (DD/MM/YYYY)
const formatIndianDate = (dateStr) => {
  if (!dateStr) return '-';
  const date = new Date(dateStr);
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();
  return `${day}/${month}/${year}`;
};

const DURATION_OPTIONS = [
  { value: 3, label: '3 Months' },
  { value: 6, label: '6 Months' },
  { value: 12, label: '12 Months' }
];

const BILLING_OPTIONS = [
  { value: 'monthly', label: 'Monthly' },
  { value: 'quarterly', label: 'Quarterly' },
  { value: 'upfront', label: 'Full Upfront' }
];

const BrandStaffContractsPage = () => {
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [contracts, setContracts] = useState([]);
  const [stores, setStores] = useState([]);
  const [manufacturers, setManufacturers] = useState([]);
  const [staffSettings, setStaffSettings] = useState(null);
  const [bookingSettings, setBookingSettings] = useState(null);
  
  // Filters
  const [filterStore, setFilterStore] = useState('all');
  const [filterManufacturer, setFilterManufacturer] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  
  // Dialog states
  const [dialogOpen, setDialogOpen] = useState(false);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [selectedContract, setSelectedContract] = useState(null);
  const [saving, setSaving] = useState(false);
  
  // Form state
  const [formData, setFormData] = useState({
    store_id: '',
    manufacturer_id: '',
    staff_type: 'our_staff',
    staff_count: 1,
    staff_details: [{ name: '', phone: '', id_proof_type: '', id_proof_number: '' }],
    contract_months: 6,
    start_date: new Date().toISOString().split('T')[0],
    billing_type: 'monthly',
    custom_discount: null,
    notes: ''
  });
  
  const [pricing, setPricing] = useState(null);

  // Fetch contracts
  const fetchContracts = useCallback(async () => {
    const token = localStorage.getItem('access_token');
    try {
      let url = `${API_URL}/api/bookings/staff-contracts?page=1&page_size=100`;
      if (filterStore !== 'all') url += `&store_id=${filterStore}`;
      if (filterManufacturer !== 'all') url += `&manufacturer_id=${filterManufacturer}`;
      if (filterStatus !== 'all') url += `&status=${filterStatus}`;
      
      const response = await fetch(url, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      setContracts(data.items || []);
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to fetch contracts', variant: 'destructive' });
    }
  }, [filterStore, filterManufacturer, filterStatus, toast]);

  // Fetch dropdown data
  const fetchDropdownData = useCallback(async () => {
    try {
      const [storesRes, mfgrRes] = await Promise.all([
        storesAPI.list(),
        manufacturersAPI.dropdown()
      ]);
      setStores(storesRes.data || []);
      setManufacturers(mfgrRes.data || []);
    } catch (error) {
      console.error('Failed to fetch dropdown data:', error);
    }
  }, []);

  // Fetch settings
  const fetchSettings = useCallback(async () => {
    const token = localStorage.getItem('access_token');
    try {
      const [staffRes, bookingRes] = await Promise.all([
        fetch(`${API_URL}/api/bookings/staff-settings`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(`${API_URL}/api/bookings/settings`, {
          headers: { 'Authorization': `Bearer ${token}` }
        })
      ]);
      
      setStaffSettings(await staffRes.json());
      setBookingSettings(await bookingRes.json());
    } catch (error) {
      console.error('Failed to fetch settings:', error);
    }
  }, []);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([fetchDropdownData(), fetchSettings()]);
      setLoading(false);
    };
    loadData();
  }, [fetchDropdownData, fetchSettings]);

  useEffect(() => {
    if (!loading) {
      fetchContracts();
    }
  }, [loading, fetchContracts]);

  // Calculate pricing
  useEffect(() => {
    if (!staffSettings || !formData.staff_count || !formData.contract_months) {
      setPricing(null);
      return;
    }
    
    const rate = formData.staff_type === 'our_staff' 
      ? staffSettings.our_staff_rate 
      : staffSettings.their_staff_rate;
    
    // Get discount from slabs
    let discountPercent = formData.custom_discount;
    if (discountPercent === null && bookingSettings?.discount_slabs) {
      const sortedSlabs = [...bookingSettings.discount_slabs].sort((a, b) => b.duration_months - a.duration_months);
      for (const slab of sortedSlabs) {
        if (formData.contract_months >= slab.duration_months) {
          discountPercent = slab.discount_percent;
          break;
        }
      }
    }
    discountPercent = discountPercent || 0;
    
    const discountedRate = rate * (1 - discountPercent / 100);
    const monthlySubtotal = discountedRate * formData.staff_count;
    const taxPercent = staffSettings.staff_tax_percent || 18;
    const monthlyTax = monthlySubtotal * (taxPercent / 100);
    const totalMonthlyCharge = monthlySubtotal + monthlyTax;
    const totalContractValue = totalMonthlyCharge * formData.contract_months;
    
    setPricing({
      rate_per_staff: rate,
      discount_percent: discountPercent,
      discounted_rate: discountedRate,
      monthly_subtotal: monthlySubtotal,
      tax_percent: taxPercent,
      monthly_tax: monthlyTax,
      total_monthly_charge: totalMonthlyCharge,
      total_contract_value: totalContractValue
    });
  }, [staffSettings, bookingSettings, formData.staff_type, formData.staff_count, formData.contract_months, formData.custom_discount]);

  // Handle staff count change
  const handleStaffCountChange = (count) => {
    const newCount = Math.max(1, Math.min(10, count));
    const currentDetails = [...formData.staff_details];
    
    while (currentDetails.length < newCount) {
      currentDetails.push({ name: '', phone: '', id_proof_type: '', id_proof_number: '' });
    }
    while (currentDetails.length > newCount) {
      currentDetails.pop();
    }
    
    setFormData(p => ({ ...p, staff_count: newCount, staff_details: currentDetails }));
  };

  // Update staff detail
  const updateStaffDetail = (index, field, value) => {
    const newDetails = [...formData.staff_details];
    newDetails[index] = { ...newDetails[index], [field]: value };
    setFormData(p => ({ ...p, staff_details: newDetails }));
  };

  // Calculate end date (ISO format)
  const calculateEndDate = (startDate, months) => {
    const start = new Date(startDate);
    start.setMonth(start.getMonth() + months);
    start.setDate(start.getDate() - 1);
    return start.toISOString().split('T')[0];
  };

  // Calculate end date in Indian format for display
  const calculateEndDateIndian = (startDate, months) => {
    const endDateISO = calculateEndDate(startDate, months);
    return formatIndianDate(endDateISO);
  };

  // Get days until expiry
  const getDaysUntilExpiry = (endDate) => {
    const end = new Date(endDate);
    const today = new Date();
    const diffTime = end - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  // Get expiry status badge
  const getExpiryBadge = (endDate) => {
    const days = getDaysUntilExpiry(endDate);
    if (days < 0) {
      return <Badge className="bg-red-100 text-red-700">Expired</Badge>;
    } else if (days <= 7) {
      return <Badge className="bg-orange-100 text-orange-700">Expires in {days}d</Badge>;
    } else if (days <= 30) {
      return <Badge className="bg-yellow-100 text-yellow-700">Expires in {days}d</Badge>;
    }
    return <Badge className="bg-green-100 text-green-700">Active</Badge>;
  };

  // Open create dialog
  const handleOpenDialog = () => {
    setFormData({
      store_id: '',
      manufacturer_id: '',
      staff_type: 'our_staff',
      staff_count: 1,
      staff_details: [{ name: '', phone: '', id_proof_type: '', id_proof_number: '' }],
      contract_months: 6,
      start_date: new Date().toISOString().split('T')[0],
      billing_type: 'monthly',
      custom_discount: null,
      notes: ''
    });
    setDialogOpen(true);
  };

  // Handle confirm
  const handleConfirm = () => {
    if (!formData.store_id || !formData.manufacturer_id) {
      toast({ title: 'Error', description: 'Please select store and manufacturer', variant: 'destructive' });
      return;
    }
    setConfirmDialogOpen(true);
  };

  // Create contract
  const handleCreateContract = async () => {
    setSaving(true);
    const token = localStorage.getItem('access_token');
    
    try {
      const response = await fetch(`${API_URL}/api/bookings/staff-contracts`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          ...formData,
          discount_percent: pricing?.discount_percent || null
        })
      });
      
      const data = await response.json();
      
      if (response.ok) {
        toast({ title: 'Success', description: `Contract ${data.contract_number} created. Invoice generated.` });
        setConfirmDialogOpen(false);
        setDialogOpen(false);
        fetchContracts();
      } else {
        toast({ title: 'Error', description: data.detail || 'Failed to create contract', variant: 'destructive' });
      }
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to create contract', variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  // View contract
  const handleViewContract = (contract) => {
    setSelectedContract(contract);
    setViewDialogOpen(true);
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'active':
        return <Badge className="bg-green-100 text-green-700">Active</Badge>;
      case 'expired':
        return <Badge className="bg-gray-100 text-gray-700">Expired</Badge>;
      case 'terminated':
        return <Badge className="bg-red-100 text-red-700">Terminated</Badge>;
      default:
        return <Badge>{status}</Badge>;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6" data-testid="brand-staff-contracts-page">
      <Toaster />
      
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Brand Staff Contracts</h1>
          <p className="text-gray-500 text-sm">Manage brand marketing staff hiring contracts</p>
        </div>
        <Button onClick={handleOpenDialog} className="bg-indigo-600 hover:bg-indigo-700">
          <Plus className="w-4 h-4 mr-2" /> New Contract
        </Button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="bg-gradient-to-br from-indigo-500 to-indigo-600 text-white">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-indigo-100 text-sm">Active Contracts</p>
                <p className="text-2xl font-bold">{contracts.filter(c => c.status === 'active').length}</p>
              </div>
              <FileText className="w-8 h-8 text-indigo-200" />
            </div>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-emerald-500 to-emerald-600 text-white">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-emerald-100 text-sm">Total Staff</p>
                <p className="text-2xl font-bold">
                  {contracts.filter(c => c.status === 'active').reduce((sum, c) => sum + (c.staff_count || 0), 0)}
                </p>
              </div>
              <Users className="w-8 h-8 text-emerald-200" />
            </div>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-amber-500 to-amber-600 text-white">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-amber-100 text-sm">Our Staff Rate</p>
                <p className="text-2xl font-bold">₹{staffSettings?.our_staff_rate?.toLocaleString() || 0}</p>
              </div>
              <DollarSign className="w-8 h-8 text-amber-200" />
            </div>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-purple-500 to-purple-600 text-white">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-purple-100 text-sm">Their Staff Rate</p>
                <p className="text-2xl font-bold">₹{staffSettings?.their_staff_rate?.toLocaleString() || 0}</p>
              </div>
              <DollarSign className="w-8 h-8 text-purple-200" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card className="bg-white border-gray-200">
        <CardContent className="p-4">
          <div className="flex flex-wrap gap-4 items-end">
            <div className="flex-1 min-w-[200px]">
              <Label className="text-gray-600 text-sm">Store</Label>
              <Select value={filterStore} onValueChange={setFilterStore}>
                <SelectTrigger className="bg-gray-50 border-gray-300">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-white">
                  <SelectItem value="all">All Stores</SelectItem>
                  {stores.map(store => (
                    <SelectItem key={store.id} value={store.id}>{store.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex-1 min-w-[200px]">
              <Label className="text-gray-600 text-sm">Manufacturer</Label>
              <Select value={filterManufacturer} onValueChange={setFilterManufacturer}>
                <SelectTrigger className="bg-gray-50 border-gray-300">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-white">
                  <SelectItem value="all">All Manufacturers</SelectItem>
                  {manufacturers.map(m => (
                    <SelectItem key={m.id} value={m.id}>{m.company_name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex-1 min-w-[150px]">
              <Label className="text-gray-600 text-sm">Status</Label>
              <Select value={filterStatus} onValueChange={setFilterStatus}>
                <SelectTrigger className="bg-gray-50 border-gray-300">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-white">
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="expired">Expired</SelectItem>
                  <SelectItem value="terminated">Terminated</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Button 
              variant="outline" 
              onClick={fetchContracts}
              className="border-gray-300"
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Refresh
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Contracts Table */}
      <Card className="bg-white border-gray-200">
        <CardContent className="p-0">
          {contracts.length === 0 ? (
            <div className="py-12 text-center">
              <Users className="w-12 h-12 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500 text-lg">No contracts found</p>
              <p className="text-gray-400 text-sm">Create your first brand staff contract</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="bg-gray-50 border-gray-200">
                  <TableHead className="text-gray-600">Contract #</TableHead>
                  <TableHead className="text-gray-600">Manufacturer</TableHead>
                  <TableHead className="text-gray-600">Store</TableHead>
                  <TableHead className="text-gray-600">Staff</TableHead>
                  <TableHead className="text-gray-600">Start Date</TableHead>
                  <TableHead className="text-gray-600">End Date</TableHead>
                  <TableHead className="text-gray-600">Monthly</TableHead>
                  <TableHead className="text-gray-600">Status</TableHead>
                  <TableHead className="text-gray-600">Payment</TableHead>
                  <TableHead className="text-gray-600 text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {contracts.map(contract => (
                  <TableRow key={contract.id} className="border-gray-200 hover:bg-gray-50">
                    <TableCell className="font-medium text-indigo-600">{contract.contract_number}</TableCell>
                    <TableCell className="text-gray-600">{contract.manufacturer_name}</TableCell>
                    <TableCell className="text-gray-600">{contract.store_name}</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Badge variant="outline" className="border-gray-300">
                          {contract.staff_count} {contract.staff_type === 'our_staff' ? 'Our' : 'Their'}
                        </Badge>
                      </div>
                    </TableCell>
                    <TableCell className="text-gray-600 text-sm">
                      {formatIndianDate(contract.start_date)}
                    </TableCell>
                    <TableCell className="font-medium text-amber-700 text-sm">
                      {formatIndianDate(contract.end_date)}
                    </TableCell>
                    <TableCell className="font-medium text-gray-800">
                      ₹{contract.total_monthly_charge?.toLocaleString()}
                    </TableCell>
                    <TableCell>{getExpiryBadge(contract.end_date)}</TableCell>
                    <TableCell>
                      <Badge className={
                        contract.payment_status === 'paid' 
                          ? 'bg-green-100 text-green-700'
                          : contract.payment_status === 'partial'
                          ? 'bg-yellow-100 text-yellow-700'
                          : 'bg-red-100 text-red-700'
                      }>
                        <CreditCard className="w-3 h-3 mr-1" />
                        {contract.payment_status || 'Pending'}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <Button variant="ghost" size="sm" onClick={() => handleViewContract(contract)}>
                        <Eye className="w-4 h-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Create Contract Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-white max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-gray-800">
              <Users className="w-5 h-5 text-indigo-600" />
              New Brand Staff Contract
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {/* Manufacturer & Store */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-gray-700">Manufacturer *</Label>
                <Select 
                  value={formData.manufacturer_id} 
                  onValueChange={(v) => setFormData(p => ({ ...p, manufacturer_id: v }))}
                >
                  <SelectTrigger className="bg-gray-50 border-gray-300">
                    <SelectValue placeholder="Select manufacturer" />
                  </SelectTrigger>
                  <SelectContent className="bg-white z-[9999]">
                    {manufacturers.map(m => (
                      <SelectItem key={m.id} value={m.id}>{m.company_name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="text-gray-700">Store *</Label>
                <Select 
                  value={formData.store_id} 
                  onValueChange={(v) => setFormData(p => ({ ...p, store_id: v }))}
                >
                  <SelectTrigger className="bg-gray-50 border-gray-300">
                    <SelectValue placeholder="Select store" />
                  </SelectTrigger>
                  <SelectContent className="bg-white z-[9999]">
                    {stores.map(s => (
                      <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Staff Type */}
            <div className="space-y-2">
              <Label className="text-gray-700">Staff Type</Label>
              <div className="grid grid-cols-2 gap-4">
                <button
                  type="button"
                  onClick={() => setFormData(p => ({ ...p, staff_type: 'our_staff' }))}
                  className={`p-4 rounded-lg border-2 text-left transition-colors ${
                    formData.staff_type === 'our_staff'
                      ? 'border-indigo-500 bg-indigo-50'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <p className="font-semibold text-gray-800">Our Staff</p>
                  <p className="text-sm text-gray-500">We provide trained marketing staff</p>
                  <p className="text-indigo-600 font-medium mt-1">₹{staffSettings?.our_staff_rate?.toLocaleString()}/person/month</p>
                </button>
                <button
                  type="button"
                  onClick={() => setFormData(p => ({ ...p, staff_type: 'their_staff' }))}
                  className={`p-4 rounded-lg border-2 text-left transition-colors ${
                    formData.staff_type === 'their_staff'
                      ? 'border-indigo-500 bg-indigo-50'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <p className="font-semibold text-gray-800">Their Staff</p>
                  <p className="text-sm text-gray-500">Manufacturer provides their own staff</p>
                  <p className="text-indigo-600 font-medium mt-1">₹{staffSettings?.their_staff_rate?.toLocaleString()}/person/month</p>
                </button>
              </div>
            </div>

            {/* Staff Count & Duration */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-gray-700">Number of Staff</Label>
                <div className="flex items-center gap-2">
                  <Button 
                    type="button" 
                    variant="outline" 
                    size="icon"
                    onClick={() => handleStaffCountChange(formData.staff_count - 1)}
                    className="border-gray-300"
                  >
                    -
                  </Button>
                  <Input
                    type="number"
                    value={formData.staff_count}
                    onChange={(e) => handleStaffCountChange(parseInt(e.target.value) || 1)}
                    className="w-20 text-center bg-gray-50 border-gray-300"
                    min={1}
                    max={10}
                  />
                  <Button 
                    type="button" 
                    variant="outline" 
                    size="icon"
                    onClick={() => handleStaffCountChange(formData.staff_count + 1)}
                    className="border-gray-300"
                  >
                    +
                  </Button>
                </div>
              </div>
              <div className="space-y-2">
                <Label className="text-gray-700">Contract Duration</Label>
                <Select 
                  value={String(formData.contract_months)} 
                  onValueChange={(v) => setFormData(p => ({ ...p, contract_months: parseInt(v) }))}
                >
                  <SelectTrigger className="bg-gray-50 border-gray-300">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-white">
                    {DURATION_OPTIONS.map(opt => (
                      <SelectItem key={opt.value} value={String(opt.value)}>{opt.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Staff Details */}
            {formData.staff_type === 'their_staff' && formData.staff_details.map((staff, idx) => (
              <div key={idx} className="p-3 bg-gray-50 rounded-lg border border-gray-200">
                <p className="text-sm font-medium text-gray-700 mb-2">Staff {idx + 1}</p>
                <div className="grid grid-cols-2 gap-3">
                  <Input
                    placeholder="Name"
                    value={staff.name}
                    onChange={(e) => updateStaffDetail(idx, 'name', e.target.value)}
                    className="bg-white border-gray-300"
                  />
                  <Input
                    placeholder="Phone"
                    value={staff.phone}
                    onChange={(e) => updateStaffDetail(idx, 'phone', e.target.value)}
                    className="bg-white border-gray-300"
                  />
                </div>
              </div>
            ))}

            {/* Start Date & Billing */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-gray-700">Start Date</Label>
                <Input
                  type="date"
                  value={formData.start_date}
                  onChange={(e) => setFormData(p => ({ ...p, start_date: e.target.value }))}
                  min={new Date().toISOString().split('T')[0]}
                  className="bg-gray-50 border-gray-300"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-gray-700">Billing Type</Label>
                <Select 
                  value={formData.billing_type} 
                  onValueChange={(v) => setFormData(p => ({ ...p, billing_type: v }))}
                >
                  <SelectTrigger className="bg-gray-50 border-gray-300">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-white">
                    {BILLING_OPTIONS.map(opt => (
                      <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Pricing Summary */}
            {pricing && (
              <div className="p-4 bg-indigo-50 rounded-lg border border-indigo-200">
                <h4 className="font-semibold text-indigo-800 mb-3 flex items-center gap-2">
                  <DollarSign className="w-4 h-4" />
                  Contract Summary
                </h4>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between text-gray-600">
                    <span>Rate per Staff:</span>
                    <span>₹{pricing.rate_per_staff?.toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between text-gray-600">
                    <span>Staff Count:</span>
                    <span>× {formData.staff_count}</span>
                  </div>
                  {pricing.discount_percent > 0 && (
                    <div className="flex justify-between text-green-600">
                      <span>Discount ({pricing.discount_percent}%):</span>
                      <span>-₹{((pricing.rate_per_staff - pricing.discounted_rate) * formData.staff_count).toLocaleString()}</span>
                    </div>
                  )}
                  <div className="flex justify-between text-gray-600">
                    <span>Monthly Subtotal:</span>
                    <span>₹{pricing.monthly_subtotal?.toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between text-gray-600">
                    <span>Tax ({pricing.tax_percent}%):</span>
                    <span>₹{pricing.monthly_tax?.toLocaleString()}</span>
                  </div>
                  <div className="border-t border-indigo-200 pt-2 mt-2">
                    <div className="flex justify-between font-semibold text-indigo-800">
                      <span>Monthly Total:</span>
                      <span>₹{pricing.total_monthly_charge?.toLocaleString()}</span>
                    </div>
                  </div>
                  <div className="flex justify-between font-bold text-indigo-900 text-base pt-2">
                    <span>Contract Total ({formData.contract_months} months):</span>
                    <span>₹{pricing.total_contract_value?.toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between text-gray-500 text-xs pt-1">
                    <span>End Date:</span>
                    <span className="font-medium text-amber-700">{calculateEndDateIndian(formData.start_date, formData.contract_months)}</span>
                  </div>
                </div>
              </div>
            )}

            {/* Notes */}
            <div className="space-y-2">
              <Label className="text-gray-700">Notes (Optional)</Label>
              <Textarea
                value={formData.notes}
                onChange={(e) => setFormData(p => ({ ...p, notes: e.target.value }))}
                placeholder="Any additional notes..."
                className="bg-gray-50 border-gray-300"
                rows={2}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)} className="border-gray-300">
              Cancel
            </Button>
            <Button 
              onClick={handleConfirm}
              disabled={!formData.store_id || !formData.manufacturer_id}
              className="bg-indigo-600 hover:bg-indigo-700"
            >
              <Check className="w-4 h-4 mr-2" />
              Create Contract
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View Contract Dialog */}
      <Dialog open={viewDialogOpen} onOpenChange={setViewDialogOpen}>
        <DialogContent className="bg-white max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-gray-800">Contract Details</DialogTitle>
          </DialogHeader>
          {selectedContract && (
            <div className="space-y-4 py-4">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-gray-500">Contract Number</p>
                  <p className="font-medium text-indigo-600">{selectedContract.contract_number}</p>
                </div>
                <div>
                  <p className="text-gray-500">Status</p>
                  {getExpiryBadge(selectedContract.end_date)}
                </div>
                <div>
                  <p className="text-gray-500">Manufacturer</p>
                  <p className="font-medium text-gray-800">{selectedContract.manufacturer_name}</p>
                </div>
                <div>
                  <p className="text-gray-500">Store</p>
                  <p className="font-medium text-gray-800">{selectedContract.store_name}</p>
                </div>
                <div>
                  <p className="text-gray-500">Staff Type</p>
                  <p className="font-medium text-gray-800">
                    {selectedContract.staff_type === 'our_staff' ? 'Our Staff' : 'Their Staff'}
                  </p>
                </div>
                <div>
                  <p className="text-gray-500">Staff Count</p>
                  <p className="font-medium text-gray-800">{selectedContract.staff_count}</p>
                </div>
                <div>
                  <p className="text-gray-500">Start Date</p>
                  <p className="font-medium text-gray-800">{formatIndianDate(selectedContract.start_date)}</p>
                </div>
                <div>
                  <p className="text-gray-500">End Date</p>
                  <p className="font-medium text-amber-700">{formatIndianDate(selectedContract.end_date)}</p>
                </div>
                <div>
                  <p className="text-gray-500">Monthly Charge</p>
                  <p className="font-medium text-gray-800">₹{selectedContract.total_monthly_charge?.toLocaleString()}</p>
                </div>
                <div>
                  <p className="text-gray-500">Total Contract Value</p>
                  <p className="font-medium text-indigo-600">₹{selectedContract.total_contract_value?.toLocaleString()}</p>
                </div>
              </div>
              
              {/* Payment Status */}
              <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <CreditCard className="w-4 h-4 text-blue-600" />
                    <span className="text-sm font-medium text-blue-800">Payment Status</span>
                  </div>
                  <Badge className={
                    selectedContract.payment_status === 'paid' 
                      ? 'bg-green-100 text-green-700'
                      : selectedContract.payment_status === 'partial'
                      ? 'bg-yellow-100 text-yellow-700'
                      : 'bg-red-100 text-red-700'
                  }>
                    {selectedContract.payment_status || 'Pending'}
                  </Badge>
                </div>
              </div>
              
              {selectedContract.staff_details?.length > 0 && (
                <div className="border-t border-gray-200 pt-4">
                  <p className="text-sm font-medium text-gray-700 mb-2">Staff Details</p>
                  <div className="space-y-2">
                    {selectedContract.staff_details.map((staff, idx) => (
                      <div key={idx} className="p-2 bg-gray-50 rounded text-sm">
                        <p className="font-medium">{staff.name || `Staff ${idx + 1}`}</p>
                        {staff.phone && <p className="text-gray-500">{staff.phone}</p>}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Confirmation Dialog */}
      <AlertDialog open={confirmDialogOpen} onOpenChange={setConfirmDialogOpen}>
        <AlertDialogContent className="bg-white">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertCircle className="w-5 h-5 text-amber-500" />
              Confirm Contract
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-3">
                <p>Please confirm the contract details:</p>
                <div className="p-3 bg-gray-50 rounded-lg text-sm space-y-1">
                  <p><strong>Manufacturer:</strong> {manufacturers.find(m => m.id === formData.manufacturer_id)?.company_name}</p>
                  <p><strong>Store:</strong> {stores.find(s => s.id === formData.store_id)?.name}</p>
                  <p><strong>Staff:</strong> {formData.staff_count} {formData.staff_type === 'our_staff' ? 'Our Staff' : 'Their Staff'}</p>
                  <p><strong>Duration:</strong> {formData.contract_months} months</p>
                  <p><strong>Period:</strong> {formatIndianDate(formData.start_date)} to {calculateEndDateIndian(formData.start_date, formData.contract_months)}</p>
                  {pricing && (
                    <>
                      <p><strong>Monthly Charge:</strong> ₹{pricing.total_monthly_charge?.toLocaleString()}</p>
                      <p className="text-indigo-700 font-semibold"><strong>Total Value:</strong> ₹{pricing.total_contract_value?.toLocaleString()}</p>
                    </>
                  )}
                </div>
                <p className="text-sm text-gray-600">
                  An invoice for the first month will be generated immediately.
                </p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="border-gray-300">Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleCreateContract}
              disabled={saving}
              className="bg-indigo-600 hover:bg-indigo-700"
            >
              {saving ? 'Creating...' : 'Confirm & Create'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default BrandStaffContractsPage;
